import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Container,
  Table,
  Button,
  Form,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Alert,
  Input,
} from "reactstrap";
import "react-datepicker/dist/react-datepicker.css";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import CommonInput from "../../Components/Common/CommonInput";
import { useFormik } from "formik";
import { FiSend, FiFileText } from "react-icons/fi";
import { FaCloudDownloadAlt } from "react-icons/fa";
import { Tooltip, OverlayTrigger } from "react-bootstrap";

import { APIClient, exportReport } from "../../helpers/api_helper";
import {
  AEDINR,
  GENERATE_PDF,
  PLAN_PRICING,
  SUPER_ADMIN_EMPLOYEE_DATATABLE,
} from "../../helpers/url_helper";
import * as Yup from "yup";
import { DatePicker } from "antd";
import { handleDownload } from "../../Components/Common/validationSchema";
import Toast from "../../Components/Common/Toast";
import { ToastContainer } from "react-toastify";
import { getCurrencyExchangeRate } from "../../Components/Common/ApiUtils";

const api = new APIClient();

const QuotationManagement = () => {
  const [pricing, setPricing] = useState({ userCost: 1, storageCost: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [employees, setEmployees] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [amountPerUserInr, setAmountPerUserInr] = useState(null);
  const [submissionType, setSubmissionType] = useState("generate"); // Track type


  const getValidationSchema = () => {
    return Yup.object({
      name: Yup.string().required("Client Name is required"),
      phone: Yup.string()
        .matches(/^[0-9]{10}$/, "Enter a valid 10-digit phone number")
        .required("Contact Number is required"),
      ...(submissionType === "send" && {
        cc1: Yup.string().email("Enter a valid email").required("Client Email is required"),
        address1: Yup.string().required("Address Line 1 is required"),
        organizationId: Yup.string().required("Organization ID is required"),
        title: Yup.string().required("Title is required"),
      }),
      discount: Yup.number()
        .min(0, "Discount cannot be negative")
        .max(100, "Maximum discount allowed is 100%"),
    });
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      user: 1,
      userCost: pricing.userCost,
      userFreq: 1,
      storage: 1,
      storageCost: pricing.storageCost,
      storageFreq: 1,
      discount: 0,
      name: "",
      address1: "",
      address2: "",
      address3: "",
      cc1: "",
      cc2: "",
      phone: "",
      expiryDate: null,
      organizationId: "",
      showDiscountOnQuotation: false,
    },
    validationSchema: getValidationSchema(),
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      const requestData = {
        title: values.title || "",
        user: {
          quantity: values.user.toString(),
          cost: values.userCost.toString(),
          frequency: values.userFreq.toString(),
          total: (values.user * values.userCost * values.userFreq).toString(),
        },
        Storage: {
          quantity: `${values.storage} GB`,
          cost: values.storageCost.toString(),
          frequency: values.storageFreq.toString(),
          total: (
            values.storage *
            values.storageCost).toString(),
        },
        finalAmount: discountedTotal.toFixed(2),
        discount: (totalCost - discountedTotal).toFixed(2).toString(),
        clientName: values.name,
        clientMobile: values.phone,
        address1: values.address1,
        address2: values.address2 || "",
        address3: values.address3 || "",
        email1: values.cc1,
        email2: values.cc2 || "",
        ...(values.expiryDate && {
          expiryDate: values.expiryDate.format("YYYY-MM-DD"),
        }),
        ...(values.organizationId && { organizationId: values.organizationId }),
        type: submissionType, // Set dynamically
        showDiscountOnQuotation: values.showDiscountOnQuotation,
      };
  
      try {
        if (submissionType === "send") {
          await sendEmail(requestData);
        } else {
          await generatePDF(requestData);
        }
      } catch (error) {
        console.error("Form submission error:", error);
        Toast("Failed to submit form", "error");
      } finally {
        setSubmitting(false);
      }
    },
  });
  
  const generatePDF = async (formData) => {
    try {
      setLoading(true);
      const response = await exportReport(GENERATE_PDF, formData);
  
      if (response) {
        handleDownload(response, "quotation.pdf");
        Toast("PDF generated successfully!", "success");
      } else {
        throw new Error(response.message || "Failed to generate PDF");
      }
    } catch (error) {
      Toast("Failed to generate PDF. Please try again.", "error");
      console.error("PDF generation error:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const sendEmail = async (formData) => {
    try {
      setLoading(true);
      formData.type = "send"; // Ensure type is set to "send"
  
      const response = await exportReport(GENERATE_PDF, formData);
  
      if (response) {
        Toast("Mail sent successfully!", "success");
      } else {
        throw new Error(response.message || "Failed to send email");
      }
    } catch (error) {
      Toast("Failed to send email. Please try again.", "error");
      console.error("Email sending error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationChange = (e) => {
    formik.setFieldValue("organizationId", e.target.value);
  };

 
  const fetchEmployees = async (e) => {

    if (e) e.preventDefault();
    setSubmissionType("send");
    setShowDropdown(true);
    const response = await api.create(SUPER_ADMIN_EMPLOYEE_DATATABLE,{});
    if (response.success) {
      setEmployees(response.data.organization || []);
    }
    formik.validateForm().then(async (errors) => {

      if (Object.keys(errors).length === 0) {
        try {

         
          formik.handleSubmit(); // Submit after fetching employees
        } catch (error) {
          console.error("Error fetching employee data:", error);
          Toast("Failed to fetch employees", "error");
        }
      } else {
        Object.keys(formik.values).forEach((field) => {
          formik.setFieldTouched(field, true);
        });
        Toast("Please fill in all required fields before sending", "error");
      }
    });
  };


const handleGenerateClick = (e) => {
  e.preventDefault();
  setSubmissionType("generate");

  formik.validateForm().then((errors) => {
    if (Object.keys(errors).length === 0) {
      formik.handleSubmit();
    } else {
      Object.keys(formik.values).forEach((field) => {
        formik.setFieldTouched(field, true);
      });
      Toast("Please fill in all required fields", "error");
    }
  });
};

  useEffect(() => {
    const getPlanPricing = async () => {
      try {
        const response = await api.get(PLAN_PRICING);
        if (response.success) {
          setPricing({
            userCost: response.data[1]?.pricingPerUser || 1,
            storageCost: response.data[1]?.pricePerGB || 1,
          });
        }
      } catch (error) {
        setError("Failed to load pricing data. Please try again.");
        console.error("Pricing fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    getCurrencyExchangeRate(setAmountPerUserInr);

    getPlanPricing();
  }, []);

  const totalCost = useMemo(() => {
    let cost =
      formik.values.user * formik.values.userCost * formik.values.userFreq +
      formik.values.storage * formik.values.storageCost;
    return Math.max(0, cost);
  }, [formik.values]);

  const discountedTotal = useMemo(() => {
    let discounted = totalCost - totalCost * (formik.values.discount / 100);
    return Math.max(0, discounted);
  }, [totalCost, formik.values.discount]);

  const amountinr = discountedTotal * amountPerUserInr;

  useEffect(() => {
    formik.setFieldValue("storageFreq", formik.values.userFreq);
  }, [formik.values.userFreq]);

  return (
    <div className="relative">
      <ToastContainer />
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/50 backdrop-blur-sm">
          <Spinner className="w-12 h-12" />
        </div>
      )}

      {/* Main Content */}
      <div
        className={`page-content sn-call-report-page-top ${
          loading ? "pointer-events-none" : ""
        }`}
      >
        <Container fluid>
          <BreadCrumb
            title="Quotation Management"
            pageTitle="Manage Quotations"
          />

          {error ? (
            <Alert color="danger">{error}</Alert>
          ) : (
            <div className={loading ? "opacity-50" : ""}>
              {/* Your existing content */}
              <Card className="shadow-sm border-0">
                <CardHeader className="custom-quotation-header text-dark fw-bold">
                  Quotation Details
                </CardHeader>
                <CardBody>
                  <div className="w-50">
                    <CommonInput
                      label="Title:"
                      name="title"
                      type="text"
                      placeholder="Enter Title"
                      formik={formik}
                    />
                  </div>
                  {/* Table for Users & Storage */}
                  <Table
                    bordered
                    responsive
                    className="mt-3 text-center align-middle"
                  >
                    <thead className="table-light">
                      <tr className="fw-bold text-dark">
                        <th className="text-center">S.no</th>
                        <th className="text-center">Item</th>
                        <th className="text-center">Quantity (GB)</th>
                        <th className="text-center">Unit Cost (AED)</th>
                        <th className="text-center">Frequency (Months)</th>
                        <th className="text-center">Total Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>1</td>
                        <td>User</td>
                        <td>
                          <CommonInput
                            name="user"
                            type="number"
                            formik={formik}
                            className="text-center"
                          />
                        </td>
                        <td>
                          <CommonInput
                            name="userCost"
                            type="number"
                            formik={formik}
                            disabled
                            className="text-center"
                          />
                        </td>
                        <td>
                          <CommonInput
                            name="userFreq"
                            type="number"
                            formik={formik}
                            className="text-center"
                            onChange={(e) => {
                              formik.handleChange(e);
                              formik.setFieldValue(
                                "storageFreq",
                                e.target.value
                              );
                            }}
                          />
                        </td>
                        <td>
                          {formik.values.user *
                            formik.values.userCost *
                            formik.values.userFreq}{" "}
                          AED
                        </td>
                      </tr>
                      <tr>
                        <td>2</td>
                        <td>Storage</td>
                        <td>
                          <CommonInput
                            name="storage"
                            type="number"
                            formik={formik}
                            className="text-center"
                          />
                        </td>
                        <td>
                          <CommonInput
                            name="storageCost"
                            type="number"
                            formik={formik}
                            disabled
                            className="text-center"
                          />
                        </td>
                        <td>
                          <CommonInput
                            name="storageFreq"
                            type="number"
                            formik={formik}
                            className="text-center"
                            disabled
                          />
                        </td>
                        <td>
                          {formik.values.storage * formik.values.storageCost}{" "}
                          AED
                        </td>
                      </tr>
                    </tbody>
                  </Table>

                  {/* Cost Calculation */}
                  <Row className="mt-4">
                    <Col md={6} className="text-secondary">
                      <h6>
                        Total Cost:{" "}
                        <span className="fw-bold text-secondary">
                          {totalCost.toFixed(2)} AED
                        </span>{" "}
                        <br />
                      </h6>
                      <h6>
                        Discount cost : {}
                        <span className="fw-bold text-secondary">
                          {(totalCost - discountedTotal).toFixed(2)} AED{" "}
                        </span>
                      </h6>
                      <h6>
                        Amount in INR:{" "}
                        <span className="fw-bold text-secondary">
                        (1 AED = {amountPerUserInr?.toFixed(2)} INR)
                        </span>{" "}
                      </h6>
                    </Col>
                    <Col md={6}>
                    <div className="mb-3">
  <label className="fw-bold">Discount (%)</label>
  <input
    type="number"
    name="discount"
    className={`form-control ${
      formik.touched.discount && formik.errors.discount ? "is-invalid" : ""
    }`}
    placeholder="Enter discount"
    value={formik.values.discount === "" ? "" : formik.values.discount}
    onChange={(e) => {
      let value = e.target.value;

      // Allow empty input to let the user delete values
      if (value === "") {
        formik.setFieldValue("discount", "");
        return;
      }

      let numericValue = parseFloat(value);
      if (numericValue > 100) numericValue = 100;
      if (numericValue < 0) numericValue = 0;

      formik.setFieldValue("discount", numericValue);
    }}
    onBlur={formik.handleBlur}
  />

  {formik.touched.discount && formik.errors.discount && (
    <div className="invalid-feedback">
      Discount must be between 0 and 100
    </div>
  )}

  {/* Checkbox to Show Discount on Quotation */}
  <div className="form-check mt-2">
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id="tooltip-show-discount">Include discount on the quotation</Tooltip>}
    >
      <input
        type="checkbox"
        name="showDiscountOnQuotation"
        className="form-check-input"
        checked={formik.values.showDiscountOnQuotation}
        onChange={(e) =>
          formik.setFieldValue("showDiscountOnQuotation", e.target.checked)
        }
      />
    </OverlayTrigger>
    <label className="form-check-label" htmlFor="showDiscountOnQuotation">
      Show discount on quotation
    </label>
  </div>
</div>
                    </Col>
                  </Row>
                  <div className="mt-3 p-3 border rounded bg-light">
                    <h5 className="text-success fw-bold">
                    Final Amount in AED:{" "}
                      <span className="text-dark">
                        {discountedTotal.toFixed(2)} AED
                      </span>
                    </h5>
                    <h5 className="text-success fw-bold mt-2">
                    Final Amount in INR (Approx):{" "}
                      <span className="text-dark">
                        {parseInt(amountinr)}.00 ₹
                      </span>
                    </h5>
                  </div>
                </CardBody>
              </Card>

              {/* Billing Information Section */}
              <Card className="shadow-sm mt-4 border-0">
                <CardHeader className="custom-quotation-header text-dark fw-bold">
                  Billing Information
                </CardHeader>
                <CardBody>
                  <CommonInput
                    label={
                      <span>
                        Client Name 
                      </span>
                    }
                    placeholder="Enter Client Name For Billing"
                    name="name"
                    type="text"
                    formik={formik}
                  />

                  <Row className="mb-4 mt-4">
                    <Col md={4}>
                      <CommonInput
                        label={
                          <span>
                            Address Line 1{" "}
                          </span>
                        }
                        placeholder="Enter Address 1"
                        name="address1"
                        type="text"
                        formik={formik}
                      />
                    </Col>
                    <Col md={4}>
                      <CommonInput
                        label="Address Line 2"
                        placeholder="Enter Address 2"
                        name="address2"
                        type="text"
                        formik={formik}
                      />
                    </Col>
                    <Col md={4}>
                      <CommonInput
                        label="Address Line 3"
                        placeholder="Enter Address 3"
                        name="address3"
                        type="text"
                        formik={formik}
                      />
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Col md={4}>
                      <div className="mb-3">
                        <label>
                          Contact Number 
                        </label>
                        <input
                          type="text"
                          name="phone"
                          className={`form-control ${
                            formik.touched.phone && formik.errors.phone
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Enter Phone Number"
                          value={formik.values.phone}
                          maxLength={10} // Restricts input to 10 digits
                          pattern="[0-9]*" // Allows only numbers
                          onChange={(e) => {
                            if (/^\d{0,10}$/.test(e.target.value)) {
                              formik.setFieldValue("phone", e.target.value);
                            }
                          }}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.phone && formik.errors.phone && (
                          <div className="invalid-feedback">
                            {formik.errors.phone}
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col md={4}>
                      <CommonInput
                        label={
                          <span>
                            CC Email 1 
                          </span>
                        }
                        placeholder="Enter CC Email 1"
                        name="cc1"
                        type="email"
                        formik={formik}
                      />
                    </Col>

                    <Col md={4}>
                      <CommonInput
                        label="CC Email 2"
                        placeholder="Enter CC Email"
                        name="cc2"
                        type="email"
                        formik={formik}
                      />
                    </Col>
                  </Row>

                  {/* Expiry Date Picker */}
                  <div className="mb-3 w-100 d-flex align-items-center justify-content-between">
                    {/* Payment Link Expiry */}
                    <div className="d-flex flex-column w-25">
                      <label>Select Payment Link Expiry </label>
                      <DatePicker
                        className={`form-control ${
                          formik.touched.expiryDate && formik.errors.expiryDate
                            ? "is-invalid"
                            : ""
                        }`}
                        value={formik.values.expiryDate}
                        onChange={(dateString) =>
                          formik.setFieldValue("expiryDate", dateString)
                        }
                        onBlur={() =>
                          formik.setFieldTouched("expiryDate", true)
                        }
                        disabledDate={(current) => current.isBefore(new Date())}
                      />
                      {formik.touched.expiryDate &&
                        formik.errors.expiryDate && (
                          <div className="invalid-feedback">
                            {formik.errors.expiryDate}
                          </div>
                        )}
                    </div>

                    {/* Organization ID Dropdown */}
                    {showDropdown && (
                      <div className="border-0 ms-3 w-25 me-auto mt-2 ms-5">
                        <label className="text-start mt-1">
                          Select Organization ID{" "}
                        </label>
                        <Form>
                          <div className="mb-3">
                            <Input
                              type="select"
                              className={`form-select ${
                                formik.touched.organizationId &&
                                formik.errors.organizationId
                                  ? "is-invalid"
                                  : ""
                              }`}
                              value={formik.values.organizationId}
                              onChange={handleOrganizationChange}
                              onBlur={() =>
                                formik.setFieldTouched("organizationId", true)
                              }
                            >
                              <option value="">Choose Organization...</option>
                              {employees.map((emp, index) => (
                                <option key={index} value={emp.organizationId}>
                                  {emp.organizationId} - {emp.organizationName}
                                </option>
                              ))}
                            </Input>
                            {formik.touched.organizationId &&
                              formik.errors.organizationId && (
                                <div className="invalid-feedback">
                                  {formik.errors.organizationId}
                                </div>
                              )}
                          </div>
                        </Form>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Action Buttons */}
              <Form onSubmit={formik.handleSubmit} className="mt-4 text-center">
               
                <Button
                  type="button"
                  color="success"
                  className="px-4"
                  onClick={handleGenerateClick}
                >
                  <FaCloudDownloadAlt className="me-2"/>
                   Generate
                </Button>

                <Button
                  type="button"
                  color="primary"
                  className="ms-3 px-4"
                  onClick={fetchEmployees}
                >
                  <FiSend className="me-2" /> Send
                </Button>
              </Form>
            </div>
          )}
        </Container>
      </div>
    </div>
  );
};

export default QuotationManagement;
